<template>
  <form novalidate @submit.prevent="onSubmit">
    <LsModal title="Pause My Subscription" track-id="pause_modal">
      <template #body>
        <div class="ls-pause-modal--sections">
          <p class="ls-pause-modal--heading">Need to change your frequency?</p>
          <p class="ls-pause-modal--sections-text">
            Most of our customers need at least some standard care throughout the fall and winter
            seasons. We encourage you to switch to a different frequency that meets your
            lawn&rsquo;s needs rather than pausing service.
          </p>
          <div
            class="ls-pause-modal--sections-link"
            test-id="change-frequency"
            @click="navOut('changeFreq')"
          >
            <LsIcon type="chevron" size="md" />
            <p class="ls-pause-modal--link-text">change frequency</p>
          </div>
        </div>
        <div class="ls-pause-modal--sections">
          <p class="ls-pause-modal--heading">Just need to skip this one time?</p>
          <p class="ls-pause-modal--sections-text">
            Pausing your service will pause all your future mows for the season. If you need to skip
            this one time you can do that by clicking below.
          </p>
          <div class="ls-pause-modal--sections-link" @click="navOut('skipMow')">
            <LsIcon type="chevron" size="md" />
            <p class="ls-pause-modal--link-text">skip my mow</p>
          </div>
        </div>
        <div class="ls-pause-modal--sections ls-pause-modal-no-border">
          <p class="ls-pause-modal--heading">Still want to pause?</p>
          <p class="ls-pause-modal--sections-text">
            If you&rsquo;re looking to pause only some of your services please contact
            {{ brandName }} support.
          </p>
          <div class="ls-pause-modal--sections-link" test-id="link" @click="navOut('messageLS')">
            <LsIcon type="chevron" size="md" />
            <p class="ls-pause-modal--link-text">contact support</p>
          </div>
        </div>
        <div class="ls-pause-modal--checkbox-div">
          <p class="ls-pause-modal--skip-text">Pause service for:</p>

          <div
            v-for="option in propertyOptions"
            :key="option.value"
            class="ls-pause-modal--checkboxes"
          >
            <BCheckbox
              v-model="selectedProperties"
              name="properties"
              :native-value="option.value"
              :disabled="option.disabled"
              type="is-primary-invert"
            >
              <span class="ls-pause-modal--checkbox-label"> {{ option.label }} </span>
              <span class="ls-pause-modal--checkbox-sublabel" test-id="sublabel">
                {{
                  generateSubLabel({
                    active_mowing_schedule_id: option.active_mowing_schedule_id,
                    priced: option.priced,
                  })
                }}
              </span>
            </BCheckbox>
          </div>
        </div>
      </template>
      <template #footer
        ><LsButton
          type="submit"
          text="pause until spring"
          track-id="pause_subscription_modal-pause"
          :loading="support.loadingStatus.isUpdating"
          :disabled="!selectedProperties.length"
          test-id="button"
        />
      </template>
    </LsModal>
  </form>
</template>

<script>
import { LsButton, LsModal, LsIcon } from '@/components/common';
import { mapState, mapActions } from 'vuex';
import { SUPPORT__PAUSE_SERVICE } from '@/store/types';
import VueTypes from 'vue-types';
import _ from 'lodash';
import { lsFilters } from 'ls-vue-common';
import { themedComponentMixin } from '@/mixins';

export default {
  name: 'PauseSubscriptionModal',
  components: {
    LsButton,
    LsModal,
    LsIcon,
  },
  mixins: [themedComponentMixin],
  props: {
    activeProperties: VueTypes.array.isRequired,
    propertyOptions: VueTypes.array.isRequired,
  },
  data() {
    return {
      selectedProperties: [...this.activeProperties],
    };
  },
  computed: {
    ...mapState(['support']),
  },
  methods: {
    ...mapActions([SUPPORT__PAUSE_SERVICE]),
    generateSubLabel({ active_mowing_schedule_id, priced }) {
      const seasonStartsAtDate = this.getNextSeasonStartsAt({ priced });
      if (active_mowing_schedule_id && seasonStartsAtDate) {
        return `- Season begins ${lsFilters.date({
          date: seasonStartsAtDate,
          format: 'year',
        })}`;
      } else if (!active_mowing_schedule_id) {
        return ` - No active subscription`;
      }
      return null;
    },
    getNextSeasonStartsAt({ priced }) {
      if (_.get(priced, 'market.next_season_starts_at')) {
        return priced.market.next_season_starts_at;
      }
      return null;
    },
    navOut(link) {
      if (link === 'changeFreq') {
        this.$emit('change-frequency');
        this.$router.push({
          name: 'support',
        });
      } else {
        this.$router.push({
          name: link,
        });
      }
      this.$parent.close();
    },
    onSubmit() {
      const propertiesToSubmit = {};

      _.forEach(this.selectedProperties, (property) => {
        _.set(propertiesToSubmit, [property], true);
      });

      this[SUPPORT__PAUSE_SERVICE]({
        properties: propertiesToSubmit,
      }).then((data) => {
        if (data) {
          this.$emit('open-success', data);
          this.$parent.close();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-pause-modal--sections {
  padding: 10px 0;
  border-bottom: 2px solid $primary-light;
}

.ls-pause-modal-no-border {
  border: none;
}

.ls-pause-modal--heading {
  padding: 10px 0;
  font-weight: bold;
}

.ls-pause-modal--sections-link {
  padding: 14px 0 8px 0;
  display: inline-flex;
  cursor: pointer;
  user-select: none;
}

.ls-pause-modal--link-text {
  padding: 1px 0 0 2px;
  text-transform: uppercase;
  font-weight: bold;
  color: $primary;
}

.ls-pause-modal--checkbox-div {
  padding: 20px 0;
}

.ls-pause-modal--skip-text,
.ls-pause-modal--checkboxes {
  padding: 4px 0;
}

.ls-pause-modal--checkbox-label {
  color: $primary;
}

.ls-pause-modal--checkbox-sublabel {
  color: $gray-secondary;
  font-size: $size-6;
}

.themed-app {
  .ls-pause-modal--sections {
    border-color: $theme-color-primary-light;
  }
  .ls-pause-modal--link-text {
    color: $theme-color-primary;
  }
  .ls-pause-modal--checkbox-label {
    color: $theme-color-primary;
  }
  .ls-pause-modal--checkbox-sublabel {
    color: $theme-color-gray-dark;
    font-size: $theme-txt-body-sm;
  }
}
</style>
